import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FavoriteButton } from "./FavoriteButton";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

const TYPE_ACCENT = {
  pensamiento: "var(--type-pensamiento)",
  relato: "var(--type-relato)",
  microrelato: "var(--type-microrelato)",
  poema: "var(--type-poema)",
};

export default function TextCardGrid({ text: t, index = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const accent = TYPE_ACCENT[t.type] || "var(--rincon-secondary)";

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const delay = Math.min(index * 70, 280);

  return (
    <article
      ref={ref}
      className="relative border-2 transition-all duration-700 ease-out hover:shadow-lg group h-full flex flex-col"
      style={{
        background: "var(--rincon-surface)",
        borderColor: accent,
        borderTopWidth: "4px",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
        transitionDelay: `${delay}ms`,
      }}
      data-testid={`text-card-${t.id}`}
    >
      <div className="absolute top-3 right-3 z-10">
        <FavoriteButton
          text={t}
          className="p-2 rounded-full backdrop-blur-sm"
          style={{ background: "rgba(253,251,247,0.85)" }}
        />
      </div>
      <Link to={`/texto/${t.id}`} className="flex flex-col h-full">
        {t.image ? (
          <div
            className="aspect-[4/3] bg-cover bg-center"
            style={{
              backgroundImage: `url(${t.image})`,
              filter: "sepia(0.05)",
            }}
            aria-hidden="true"
          />
        ) : (
          <div
            className="aspect-[4/3] flex items-center justify-center"
            style={{ background: "var(--rincon-bg)" }}
            aria-hidden="true"
          >
            <span
              className="font-display text-5xl"
              style={{ color: accent, opacity: 0.35 }}
            >
              ❦
            </span>
          </div>
        )}

        <div className="p-5 md:p-6 flex flex-col flex-1">
          <div
            className="text-[10px] uppercase tracking-[0.25em] mb-2 font-semibold"
            style={{ color: accent }}
          >
            {t.subcategory} · {formatDate(t.created_at)}
          </div>
          <h3
            className="font-display text-xl md:text-2xl leading-tight tracking-tight transition-colors group-hover:text-[var(--rincon-primary)]"
            style={{ color: "var(--rincon-text)" }}
          >
            {t.title}
          </h3>
          {t.epigraph ? (
            <p
              className="mt-2 font-body italic text-sm line-clamp-2"
              style={{ color: "var(--rincon-secondary)" }}
            >
              «{t.epigraph}»
            </p>
          ) : (
            <p
              className="mt-2 font-body text-sm line-clamp-3"
              style={{ color: "var(--rincon-text-muted)" }}
            >
              {t.content.slice(0, 140)}
              {t.content.length > 140 ? "…" : ""}
            </p>
          )}
        </div>
      </Link>
    </article>
  );
}
